import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format, parseISO, isSameDay } from "date-fns";
import { api, type Book, type Meeting, type RatingsResponse, type Rsvp, type RsvpStatus } from "../api";
import { Button, Card, EmptyState, Input, SectionHeading, Spinner } from "../components/ui";
import { StarRating } from "../components/StarRating";
import { useAuth } from "../auth";

export default function CalendarPage() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const list = useQuery({
    queryKey: ["meetings"],
    queryFn: () => api.get<{ meetings: Meeting[] }>("/meetings"),
  });

  const books = useQuery({
    queryKey: ["books"],
    queryFn: () => api.get<{ books: Book[] }>("/books"),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/meetings/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["meetings"] }),
  });

  const meetings = list.data?.meetings ?? [];
  const upcoming = meetings.filter((m) => new Date(m.meetsAt).getTime() > Date.now() - 86_400_000);
  const past = meetings.filter((m) => new Date(m.meetsAt).getTime() <= Date.now() - 86_400_000);

  return (
    <div className="flex flex-col gap-10">
      <div className="flex items-end justify-between gap-4">
        <SectionHeading
          eyebrow="Save the date"
          title="Calendar"
          description="When and where we're gathering next."
        />
        <Button onClick={() => setShowForm((v) => !v)} variant={showForm ? "ghost" : "primary"}>
          {showForm ? "Cancel" : "Schedule a meeting"}
        </Button>
      </div>

      {showForm ? (
        <MeetingForm
          books={books.data?.books ?? []}
          onSaved={() => {
            setShowForm(false);
            qc.invalidateQueries({ queryKey: ["meetings"] });
          }}
        />
      ) : null}

      {list.isLoading ? (
        <Spinner />
      ) : meetings.length === 0 ? (
        <Card accent>
          <EmptyState
            title="No meetings scheduled"
            description="Pick a date and a venue and we'll keep it in the calendar."
            action={<Button onClick={() => setShowForm(true)}>Schedule the first one</Button>}
          />
        </Card>
      ) : (
        <>
          {upcoming.length > 0 ? (
            <div className="flex flex-col gap-5">
              <h3 className="text-[0.7rem] uppercase tracking-[0.18em] text-gold-dark">Upcoming</h3>
              {upcoming.map((m) => (
                <MeetingCard key={m.id} meeting={m} onDelete={() => remove.mutate(m.id)} />
              ))}
            </div>
          ) : null}
          {past.length > 0 ? (
            <div className="flex flex-col gap-5 mt-6">
              <h3 className="text-[0.7rem] uppercase tracking-[0.18em] text-plum-mute">Past meetings</h3>
              {past.map((m) => (
                <MeetingCard key={m.id} meeting={m} onDelete={() => remove.mutate(m.id)} muted />
              ))}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

function MeetingCard({
  meeting,
  onDelete,
  muted,
}: {
  meeting: Meeting;
  onDelete: () => void;
  muted?: boolean;
}) {
  const date = parseISO(meeting.meetsAt);
  const today = isSameDay(date, new Date());
  return (
    <Card className={muted ? "opacity-80" : ""}>
      <div className="flex flex-col md:flex-row md:items-start gap-5">
        <DateBlock date={date} highlight={today} />
        <div className="flex-1 min-w-0">
          <div className="font-serif text-xl text-plum">
            {meeting.bookTitle ?? "A book club meeting"}
          </div>
          <div className="text-plum-soft text-sm mt-1.5">
            {format(date, "EEEE 'at' h:mm a")}
          </div>

          <VenueEditor meeting={meeting} muted={muted} />

          {meeting.notes ? (
            <p className="text-plum-soft text-sm mt-2 italic">{meeting.notes}</p>
          ) : null}

          {!muted ? <RsvpControls meetingId={meeting.id} /> : null}
          {muted && meeting.bookId ? <PostMeetingRate bookId={meeting.bookId} /> : null}
        </div>
        <div className="flex flex-col gap-2 md:items-end self-start">
          {!muted ? <ReminderButton meetingId={meeting.id} /> : null}
          {!muted ? (
            <>
              <Link
                to={`/meetings/${meeting.id}/room`}
                className="text-xs uppercase tracking-[0.16em] px-3 py-1.5 rounded-full bg-rose text-white hover:bg-rose-dark transition-colors"
              >
                Open meeting →
              </Link>
              <Link
                to={`/meetings/${meeting.id}/packet`}
                className="text-xs uppercase tracking-[0.16em] px-3 py-1.5 rounded-full bg-blush-soft text-rose-dark hover:bg-blush transition-colors"
              >
                Host packet →
              </Link>
            </>
          ) : null}
          <button
            className="text-xs uppercase tracking-[0.16em] text-plum-mute hover:text-rose"
            onClick={onDelete}
          >
            Remove
          </button>
        </div>
      </div>
    </Card>
  );
}

function VenueEditor({ meeting, muted }: { meeting: Meeting; muted?: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(meeting.venueName ?? "");
  const [address, setAddress] = useState(meeting.venueAddress ?? "");

  const save = useMutation({
    mutationFn: (payload: { venueName: string | null; venueAddress: string | null }) =>
      api.patch(`/meetings/${meeting.id}`, payload),
    onSuccess: () => {
      setEditing(false);
      qc.invalidateQueries({ queryKey: ["meetings"] });
    },
  });

  if (editing) {
    return (
      <div className="mt-3 flex flex-col gap-2 p-3 rounded-xl bg-cream-50 border border-cream-300">
        <div className="text-[0.65rem] uppercase tracking-[0.16em] text-gold-dark">
          Edit venue
        </div>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Venue name (e.g. Witherspoon Grill)"
          className="w-full px-3 py-2 rounded-lg bg-white border border-cream-300 text-sm text-plum placeholder:text-plum-mute placeholder:italic focus:outline-none focus:border-rose"
          autoFocus
        />
        <input
          type="text"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="Address (e.g. 57 Witherspoon St, Princeton, NJ)"
          className="w-full px-3 py-2 rounded-lg bg-white border border-cream-300 text-sm text-plum placeholder:text-plum-mute placeholder:italic focus:outline-none focus:border-rose"
        />
        <div className="flex justify-end gap-2 mt-1">
          <button
            onClick={() => {
              setName(meeting.venueName ?? "");
              setAddress(meeting.venueAddress ?? "");
              setEditing(false);
            }}
            className="text-xs uppercase tracking-[0.14em] text-plum-mute hover:text-plum px-3 py-1.5"
          >
            Cancel
          </button>
          <button
            onClick={() =>
              save.mutate({
                venueName: name.trim() || null,
                venueAddress: address.trim() || null,
              })
            }
            disabled={save.isPending}
            className="text-xs uppercase tracking-[0.14em] px-4 py-1.5 rounded-full bg-rose text-white hover:bg-rose-dark disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : "Save venue"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2 flex flex-col gap-0.5">
      <div className="flex items-baseline gap-2 flex-wrap">
        {meeting.venueName ? (
          <span className="text-plum-soft text-sm">{meeting.venueName}</span>
        ) : (
          <span className="text-plum-mute text-sm italic">No venue set</span>
        )}
        {!muted ? (
          <button
            onClick={() => setEditing(true)}
            className="text-[0.65rem] uppercase tracking-[0.14em] text-plum-mute hover:text-rose"
          >
            {meeting.venueName ? "Change" : "Add venue"}
          </button>
        ) : null}
      </div>
      {meeting.venueAddress ? (
        <a
          className="text-rose text-xs underline w-fit"
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(meeting.venueAddress)}`}
          target="_blank"
          rel="noreferrer noopener"
        >
          {meeting.venueAddress}
        </a>
      ) : null}
      {meeting.venueUpdatedBy && meeting.venueUpdatedAt ? (
        <p className="text-[0.65rem] text-plum-mute italic mt-1">
          Last updated by{" "}
          <span className="text-plum-soft not-italic">
            {meeting.venueUpdatedBy.name ?? meeting.venueUpdatedBy.email.split("@")[0]}
          </span>{" "}
          · {format(parseISO(meeting.venueUpdatedAt.replace(" ", "T") + "Z"), "MMM d, h:mm a")}
        </p>
      ) : null}
    </div>
  );
}

function PostMeetingRate({ bookId }: { bookId: number }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const list = useQuery({
    queryKey: ["ratings", bookId],
    queryFn: () => api.get<RatingsResponse>(`/ratings/${bookId}`),
  });
  const set = useMutation({
    mutationFn: (stars: number) => api.post(`/ratings/${bookId}`, { stars }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ratings", bookId] }),
  });

  const mine = list.data?.ratings.find((r) => r.userId === user?.id);
  const avg = list.data?.average ?? null;

  return (
    <div className="mt-4 pt-4 border-t border-cream-200 flex items-center gap-4 flex-wrap">
      <div className="flex items-center gap-3">
        <span className="text-[0.7rem] uppercase tracking-[0.16em] text-gold-dark">
          {mine ? "Your rating" : "Rate this book"}
        </span>
        <StarRating
          value={mine?.stars ?? 0}
          onChange={(v) => set.mutate(v)}
          size={20}
        />
      </div>
      {avg ? (
        <div className="text-xs text-plum-mute flex items-center gap-2">
          Club: <StarRating value={avg} readOnly size={13} /> {avg.toFixed(1)} · {list.data!.count}
        </div>
      ) : null}
      <Link
        to={`/book/${bookId}`}
        className="ml-auto text-[0.7rem] uppercase tracking-[0.14em] text-plum-soft hover:text-rose"
      >
        Write a review →
      </Link>
    </div>
  );
}

function ReminderButton({ meetingId }: { meetingId: number }) {
  const send = useMutation({
    mutationFn: () => api.post<{ sent: number; recipients: number }>(`/meetings/${meetingId}/remind`),
  });
  return (
    <button
      onClick={() => send.mutate()}
      disabled={send.isPending}
      className="text-xs uppercase tracking-[0.14em] px-3 py-1.5 rounded-full bg-cream-100 text-plum-soft hover:bg-blush-soft hover:text-rose-dark transition-colors disabled:opacity-50"
    >
      {send.isPending
        ? "Sending…"
        : send.isSuccess
        ? `Sent to ${send.data!.sent} of ${send.data!.recipients}`
        : "Send reminder"}
    </button>
  );
}

function RsvpControls({ meetingId }: { meetingId: number }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const list = useQuery({
    queryKey: ["rsvps", meetingId],
    queryFn: () => api.get<{ rsvps: Rsvp[] }>(`/meetings/${meetingId}/rsvps`),
  });

  const set = useMutation({
    mutationFn: (payload: { status: RsvpStatus; dietary?: string | null }) =>
      api.post(`/meetings/${meetingId}/rsvp`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rsvps", meetingId] }),
  });

  const rsvps = list.data?.rsvps ?? [];
  const mine = rsvps.find((r) => r.userId === user?.id) ?? null;
  const [dietary, setDietary] = useState(mine?.dietary ?? "");
  const [showDietary, setShowDietary] = useState(false);

  const counts = {
    yes: rsvps.filter((r) => r.status === "yes").length,
    no: rsvps.filter((r) => r.status === "no").length,
    maybe: rsvps.filter((r) => r.status === "maybe").length,
  };

  function pick(status: RsvpStatus) {
    set.mutate({ status, dietary: dietary || mine?.dietary || null });
    if (status !== "no") setShowDietary(true);
  }

  return (
    <div className="mt-4 pt-4 border-t border-cream-200 flex flex-col gap-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[0.65rem] uppercase tracking-[0.16em] text-plum-mute mr-1">
          You:
        </span>
        <RsvpPill label="Yes" active={mine?.status === "yes"} onClick={() => pick("yes")} tone="rose" />
        <RsvpPill label="Maybe" active={mine?.status === "maybe"} onClick={() => pick("maybe")} tone="gold" />
        <RsvpPill label="No" active={mine?.status === "no"} onClick={() => pick("no")} tone="plum" />
        <span className="ml-auto text-xs text-plum-mute">
          {counts.yes} yes · {counts.maybe} maybe · {counts.no} no
        </span>
      </div>

      {(mine?.status === "yes" || mine?.status === "maybe" || showDietary) && (
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Any dietary notes? (optional)"
            value={dietary}
            onChange={(e) => setDietary(e.target.value)}
            onBlur={() => {
              if (mine && dietary !== (mine.dietary ?? "")) {
                set.mutate({ status: mine.status, dietary: dietary || null });
              }
            }}
            className="flex-1 px-3 py-2 rounded-lg bg-cream-50 border border-cream-300 text-sm text-plum placeholder:text-plum-mute placeholder:italic focus:outline-none focus:border-rose"
          />
        </div>
      )}

      {rsvps.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {rsvps.map((r) => (
            <span
              key={r.userId}
              className={[
                "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs",
                r.status === "yes"
                  ? "bg-blush-soft/60 text-rose-dark"
                  : r.status === "maybe"
                  ? "bg-gold/15 text-gold-dark"
                  : "bg-plum/8 text-plum-light",
              ].join(" ")}
              title={r.dietary ? `Dietary: ${r.dietary}` : undefined}
            >
              {r.status === "yes" ? "✓" : r.status === "maybe" ? "?" : "✗"} {r.name ?? r.email.split("@")[0]}
              {r.dietary ? <em className="text-[0.65rem] opacity-70">· {r.dietary}</em> : null}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function RsvpPill({
  label,
  active,
  onClick,
  tone,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  tone: "rose" | "gold" | "plum";
}) {
  const activeClass = {
    rose: "bg-rose text-white shadow-[0_2px_8px_rgba(199,123,133,0.30)]",
    gold: "bg-gold text-white",
    plum: "bg-plum text-white",
  }[tone];
  const idleClass = "bg-cream-100 text-plum-soft hover:bg-cream-200";
  return (
    <button
      onClick={onClick}
      className={[
        "px-3 py-1 rounded-full text-xs uppercase tracking-[0.14em] transition-all",
        active ? activeClass : idleClass,
      ].join(" ")}
    >
      {label}
    </button>
  );
}

function DateBlock({ date, highlight }: { date: Date; highlight?: boolean }) {
  return (
    <div
      className={[
        "shrink-0 w-20 text-center rounded-xl border p-3",
        highlight
          ? "bg-rose text-white border-rose-dark"
          : "bg-cream-50 border-cream-300 text-plum",
      ].join(" ")}
    >
      <div className={["text-[0.65rem] uppercase tracking-[0.18em] font-medium", highlight ? "text-blush-soft" : "text-gold-dark"].join(" ")}>
        {format(date, "MMM")}
      </div>
      <div className="font-serif text-3xl leading-none mt-1">{format(date, "d")}</div>
      <div className={["text-[0.65rem] mt-1", highlight ? "text-blush-soft" : "text-plum-mute"].join(" ")}>
        {format(date, "EEE")}
      </div>
    </div>
  );
}

function MeetingForm({ books, onSaved }: { books: Book[]; onSaved: () => void }) {
  const [bookId, setBookId] = useState<number | "current" | "">("current");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("19:00");
  const [venueName, setVenueName] = useState("");
  const [venueAddress, setVenueAddress] = useState("");
  const [notes, setNotes] = useState("");

  const create = useMutation({
    mutationFn: (payload: {
      bookId: number | null;
      meetsAt: string;
      venueName: string | null;
      venueAddress: string | null;
      notes: string | null;
    }) => api.post("/meetings", payload),
    onSuccess: onSaved,
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!date) return;
    const meetsAt = new Date(`${date}T${time}`).toISOString();
    const resolvedBookId =
      bookId === "current" ? books[0]?.id ?? null : bookId === "" ? null : Number(bookId);
    create.mutate({
      bookId: resolvedBookId,
      meetsAt,
      venueName: venueName || null,
      venueAddress: venueAddress || null,
      notes: notes || null,
    });
  }

  return (
    <Card accent>
      <form onSubmit={submit} className="grid md:grid-cols-2 gap-5">
        <div className="md:col-span-2">
          <label className="text-xs font-medium uppercase tracking-[0.12em] text-plum-soft block mb-1.5">
            For which book
          </label>
          <select
            value={String(bookId)}
            onChange={(e) =>
              setBookId(
                e.target.value === "current" ? "current" : e.target.value === "" ? "" : Number(e.target.value)
              )
            }
            className="w-full px-4 py-3 rounded-xl bg-cream-50 border border-cream-300 text-plum focus:outline-none focus:border-rose"
          >
            <option value="current">Current book</option>
            <option value="">No specific book</option>
            {books.map((b) => (
              <option key={b.id} value={b.id}>
                {b.title}
              </option>
            ))}
          </select>
        </div>
        <Input label="Date" type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
        <Input label="Time" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
        <Input
          label="Venue name"
          value={venueName}
          onChange={(e) => setVenueName(e.target.value)}
          placeholder="The Black Cat"
        />
        <Input
          label="Address"
          value={venueAddress}
          onChange={(e) => setVenueAddress(e.target.value)}
          placeholder="123 Main St, Princeton, NJ"
        />
        <div className="md:col-span-2">
          <label className="text-xs font-medium uppercase tracking-[0.12em] text-plum-soft block mb-1.5">
            Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Parking, dietary preferences, who's bringing what…"
            className="w-full px-4 py-3 rounded-xl bg-cream-50 border border-cream-300 text-plum focus:outline-none focus:border-rose resize-none"
          />
        </div>
        <div className="md:col-span-2 flex justify-end gap-3 pt-2">
          <Button type="submit" loading={create.isPending}>
            Save meeting
          </Button>
        </div>
        {create.error ? (
          <p className="md:col-span-2 text-sm text-rose-dark text-right">
            {(create.error as Error).message}
          </p>
        ) : null}
      </form>
    </Card>
  );
}
